import { useGetBlockEvaluationSummaryQuery } from 'common/api/endpoints/block';
import { SavedFilterOptions } from 'common/hooks';
import { FC } from 'react';
import styled from 'styled-components';
import { toFormattedNumber } from '../hooks/useVarietalSumTableData';

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
  padding: 8px 12px;
  margin-bottom: 6px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`;

const Totals = styled.div`
  display: flex;
  gap: 24px;
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
`;

const Sizes = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const Label = styled.span`
  font-family: 'KanitSemiBold';
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b6b6b;
`;

const Value = styled.span`
  font-family: 'KanitMedium';
  font-size: 18px;
`;

const Chip = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  min-width: 34px;
  padding: 2px 6px;
  border-radius: 3px;
  background: #f2f2f2;
`;

const ChipSize = styled.span`
  font-family: 'KanitSemiBold';
  font-size: 14px;
`;

const ChipPct = styled.span`
  font-family: 'KanitLight';
  font-size: 11px;
  color: #6b6b6b;
`;

/**
 * A filter-aware totals bar for an evaluation table. Sums estimated bins and the
 * bin-weighted cumulative size structure across every block matching the table's
 * current filters (all pages, not just the visible one).
 */
export const EvalSummaryBar: FC<{
  seasonId?: number;
  filters: SavedFilterOptions[];
}> = ({ seasonId, filters }) => {
  const { data, isFetching } = useGetBlockEvaluationSummaryQuery(
    { filters, seasonId: seasonId as number },
    { skip: !seasonId },
  );

  if (!seasonId) {
    return null;
  }

  const sizeStructure = (data?.sizeStructure ?? []).filter(
    structure => structure.percentValue > 0,
  );

  return (
    <Bar>
      <Totals>
        <Metric>
          <Label>Total Bins</Label>
          <Value>{isFetching ? '…' : toFormattedNumber(data?.totalBins)}</Value>
        </Metric>
        <Metric>
          <Label>Blocks</Label>
          <Value>
            {isFetching ? '…' : toFormattedNumber(data?.totalBlocks)}
          </Value>
        </Metric>
      </Totals>
      <Sizes>
        <Label>Size / %</Label>
        {sizeStructure.length === 0 ? (
          <Chip>-</Chip>
        ) : (
          sizeStructure.map(
            (structure: { size: string; percentValue: number }) => (
              <Chip key={structure.size}>
                <ChipSize>{structure.size}</ChipSize>
                <ChipPct>{structure.percentValue}%</ChipPct>
              </Chip>
            ),
          )
        )}
      </Sizes>
    </Bar>
  );
};
